import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Ring from "@/components/Ring";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { claimAnonChat, postAuthPath, activatedSummary } from "@/lib/claimAnonChat";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await register(email, password, name);
    if (res.ok) {
      // Attribute this signup to whichever voice the visitor last heard on
      // the landing hero. Fire-and-forget — never blocks the success flow.
      try {
        const v = sessionStorage.getItem("rammy_last_voice");
        if (v) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/voice-event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ voice: v, event: "signups" }),
            keepalive: true,
          }).catch(() => {});
          sessionStorage.removeItem("rammy_last_voice");
        }
      } catch { /* no-op */ }
      // "This chat, saved" is promised on every landing CTA — deliver it.
      const claimed = await claimAnonChat();
      setBusy(false);
      toast.success(claimed
        ? "Saved — our whole chat is in your account"
        : "Welcome to Rammy");
      // Anything Rammy captured before there was an account is real now.
      const switchedOn = activatedSummary(claimed);
      if (switchedOn) setTimeout(() => toast.success(switchedOn), 900);
      nav(postAuthPath(claimed));
      return;
    }
    setBusy(false);
    setError(res.error); toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex items-center justify-center relative overflow-hidden order-2" style={{ background: "var(--rm-indigo-800)" }}>
        <div className="relative text-center px-10">
          <div className="flex justify-center">
            <Ring state="idle" size={200} showLabel={false} testId="signup-ring"/>
          </div>
          <div className="mt-10 max-w-sm mx-auto">
            <div className="text-2xl font-display text-white">Nice to meet you.</div>
            <p className="mt-3 font-serif text-[17px] text-white/85">
              Tell me one thing you want to remember. I&rsquo;ll hold on to it.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <Link to="/" className="text-sm text-[var(--rammy-text-muted)] hover:underline">← Back home</Link>
          <h1 className="mt-4 text-4xl font-display">Create your Rammy</h1>
          <p className="mt-2 text-[var(--rammy-text-sec)]">Free forever plan. No card required.</p>

          <div className="mt-8">
            <GoogleSignInButton label="Sign up with Google" dataTestid="signup-google"/>
            <div className="my-5 flex items-center gap-3 text-xs text-[var(--rammy-text-muted)]">
              <div className="h-px flex-1 bg-[var(--rammy-border)]"/> or continue with email <div className="h-px flex-1 bg-[var(--rammy-border)]"/>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="signup-name" required value={name} onChange={e=>setName(e.target.value)} className="mt-2 h-12 rounded-2xl"/>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="signup-email" required value={email} onChange={e=>setEmail(e.target.value)} className="mt-2 h-12 rounded-2xl"/>
            </div>
            <div>
              <Label htmlFor="password">Password (min 6)</Label>
              <Input id="password" type="password" data-testid="signup-password" required minLength={6} value={password} onChange={e=>setPassword(e.target.value)} className="mt-2 h-12 rounded-2xl"/>
            </div>
            <Button type="submit" disabled={busy} className="btn-pill btn-primary w-full h-12" data-testid="signup-submit">
              {busy ? "Creating…" : "Create free account"}
            </Button>
            {error && (
              <div role="alert" data-testid="signup-error" className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="mt-6 text-sm text-[var(--rammy-text-sec)]">
            Already have one? <Link to="/login" className="underline underline-offset-4">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
